<template>
  <div
    class="quick-add-select flex flex-col gap-1.5 min-w-0 w-full"
    :class="{ 'quick-add-select--creating': isCreateMode }"
  >
    <InputGroup class="quick-add-select__group w-full min-w-0">
      <FloatLabel variant="on" class="flex-1 min-w-0 w-full">
        <Select
          v-if="!isCreateMode"
          :id="inputId"
          v-model="model"
          :options="options"
          :optionLabel="optionLabel"
          :optionValue="optionValue"
          class="w-full min-w-0 max-w-full"
          :class="{
            'quick-add-select__control--tag': displayVariant === 'department',
            'quick-add-select__control--rich': displayVariant === 'rich',
          }"
          inputClass="w-full min-w-0 max-w-full truncate rounded-md"
          :disabled="disabled"
          :invalid="invalid"
          :filter="filter"
          :filterFields="resolvedFilterFields"
          :showClear="showClear"
          :loading="loading"
        >
          <template v-if="displayVariant === 'department'" #option="{ option }">
            <Tag
              :value="option[optionLabel]"
              :style="departmentOptionStyle(option)"
              class="text-xs max-w-full truncate"
            />
          </template>
          <template v-if="displayVariant === 'department'" #value="{ value }">
            <Tag
              v-if="selectedOptionForValue(value)"
              :value="selectedOptionForValue(value)[optionLabel]"
              :style="departmentOptionStyle(selectedOptionForValue(value))"
              class="text-xs max-w-full truncate"
            />
          </template>
          <template v-else-if="displayVariant === 'rich'" #option="{ option }">
            <div class="quick-add-select__rich-option">
              <span class="quick-add-select__rich-label">{{ option[optionLabel] }}</span>
              <span v-if="option.meta" class="quick-add-select__rich-meta">{{ option.meta }}</span>
            </div>
          </template>
          <template v-else-if="displayVariant === 'rich'" #value="{ value }">
            <div v-if="selectedOptionForValue(value)" class="quick-add-select__rich-value">
              <span class="quick-add-select__rich-label">{{ selectedOptionForValue(value)[optionLabel] }}</span>
              <span v-if="selectedOptionForValue(value).meta" class="quick-add-select__rich-meta">
                {{ selectedOptionForValue(value).meta }}
              </span>
            </div>
          </template>
        </Select>
        <InputText
          v-else
          :id="inputId"
          ref="nameInputRef"
          v-model="newName"
          class="w-full min-w-0 max-w-full truncate rounded-md"
          :maxlength="maxLength"
          autocomplete="off"
          :disabled="disabled || isCreating"
          :invalid="!!displayError"
          :placeholder="createPlaceholder"
          :aria-label="createPlaceholder"
          @keydown.enter.prevent="confirmCreate"
          @keydown.escape.prevent="cancelCreate"
        />
        <label :for="inputId" class="text-sm font-medium">
          {{ label }}
          <span v-if="required" class="text-red-500">*</span>
        </label>
      </FloatLabel>
      <template v-if="canAdd">
        <InputGroupAddon v-if="!isCreateMode">
          <Button
            icon="pi pi-plus"
            type="button"
            severity="secondary"
            variant="text"
            class="quick-add-select__action-btn"
            :aria-label="addAriaLabel"
            :disabled="disabled || addDisabled || isCreating"
            v-tooltip.top="resolvedAddTooltip"
            @click="openCreateMode"
          />
        </InputGroupAddon>
        <template v-else>
          <InputGroupAddon>
            <Button
              icon="pi pi-check"
              type="button"
              severity="success"
              variant="text"
              class="quick-add-select__action-btn"
              :loading="isCreating"
              :disabled="disabled || isCreating || !newName.trim()"
              :aria-label="confirmAriaLabel"
              v-tooltip.top="confirmAriaLabel"
              @click="confirmCreate"
            />
          </InputGroupAddon>
          <InputGroupAddon>
            <Button
              icon="pi pi-times"
              type="button"
              severity="secondary"
              variant="text"
              class="quick-add-select__action-btn"
              :disabled="disabled || isCreating"
              aria-label="Cancel"
              v-tooltip.top="'Cancel'"
              @click="cancelCreate"
            />
          </InputGroupAddon>
        </template>
      </template>
    </InputGroup>

    <small v-if="isCreateMode && contextHint" class="text-xs text-surface-500 dark:text-surface-400 pl-1">
      {{ contextHint }}
    </small>
    <small v-if="isCreateMode && displayError" class="text-xs text-red-500 pl-1">
      {{ displayError }}
    </small>
    <small v-if="hint && !isCreateMode" class="text-xs text-surface-500 dark:text-surface-400 pl-1">
      {{ hint }}
    </small>
    <small v-if="invalid && invalidMessage && !isCreateMode" class="text-xs text-red-500 pl-1">
      {{ invalidMessage }}
    </small>

    <p class="sr-only" aria-live="polite">{{ liveMessage }}</p>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import Button from 'primevue/button';
import FloatLabel from 'primevue/floatlabel';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import Tag from 'primevue/tag';
import { normalizeReferenceId } from '@/features/org-references/utils/referenceOptionUtils';
import { getDepartmentTagStyle } from '@/utils/tagColors';

const props = defineProps({
  label: { type: String, required: true },
  options: { type: Array, default: () => [] },
  optionLabel: { type: String, default: 'name' },
  optionValue: { type: String, default: 'id' },
  required: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  invalid: { type: Boolean, default: false },
  invalidMessage: { type: String, default: '' },
  filter: { type: Boolean, default: true },
  showClear: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  canAdd: { type: Boolean, default: false },
  addDisabled: { type: Boolean, default: false },
  addDisabledMessage: { type: String, default: '' },
  addLabel: { type: String, default: '' },
  createPlaceholder: { type: String, default: 'New name' },
  contextHint: { type: String, default: '' },
  hint: { type: String, default: '' },
  maxLength: { type: Number, default: 160 },
  inputId: { type: String, default: '' },
  /** async (name) => option with id */
  createOption: { type: Function, default: null },
  /** 'department' renders colored Tag chips; 'rich' shows label + meta line */
  displayVariant: { type: String, default: 'default' },
  /** Extra Select filter fields (rich variant adds meta/searchText by default). */
  filterFields: { type: Array, default: () => [] },
});

const model = defineModel({ default: null });

const isCreateMode = ref(false);
const newName = ref('');
const isCreating = ref(false);
const localError = ref('');
const liveMessage = ref('');
const nameInputRef = ref(null);

const addAriaLabel = computed(
  () => props.addLabel || `Create ${props.label}`
);
const confirmAriaLabel = computed(
  () => props.addLabel || `Add ${props.label}`
);

const resolvedAddTooltip = computed(() => {
  if (props.addDisabled && props.addDisabledMessage) {
    return props.addDisabledMessage;
  }
  return addAriaLabel.value;
});

const displayError = computed(() => localError.value);

const resolvedFilterFields = computed(() => {
  if (props.filterFields.length) return props.filterFields;
  if (props.displayVariant === 'rich') {
    return [props.optionLabel, 'meta', 'searchText'];
  }
  return undefined;
});

function departmentOptionStyle(option) {
  if (!option) return {};
  return getDepartmentTagStyle(
    option.id,
    option.name ?? option.label,
    option.tag_color ?? null,
    option.tag_text_color ?? null
  );
}

function selectedOptionForValue(value) {
  if (value == null || value === '') return null;
  const target = normalizeReferenceId(value) ?? value;
  return (props.options || []).find(
    (row) => String(normalizeReferenceId(row?.[props.optionValue]) ?? row?.[props.optionValue]) === String(target)
  ) || null;
}

function focusNameInput() {
  nextTick(() => {
    const inst = nameInputRef.value;
    const root = inst?.$el ?? inst;
    const input = root?.querySelector?.('input') ?? root;
    if (input?.focus) input.focus();
  });
}

function openCreateMode() {
  if (props.disabled || props.addDisabled || !props.canAdd || isCreating.value) return;
  isCreateMode.value = true;
  newName.value = '';
  localError.value = '';
  focusNameInput();
}

function cancelCreate() {
  if (isCreating.value) return;
  isCreateMode.value = false;
  newName.value = '';
  localError.value = '';
}

async function confirmCreate() {
  if (typeof props.createOption !== 'function' || isCreating.value) return;
  const name = newName.value.trim();
  if (!name) {
    localError.value = 'Name is required';
    return;
  }

  isCreating.value = true;
  localError.value = '';
  try {
    const option = await props.createOption(name);
    const id =
      normalizeReferenceId(option?.id) ??
      normalizeReferenceId(option?.value) ??
      option?.id ??
      option?.value ??
      null;
    if (id != null) {
      model.value = id;
    }
    const displayName = option?.name || option?.label || name;
    liveMessage.value = `${props.label} “${displayName}” selected.`;
    isCreateMode.value = false;
    newName.value = '';
  } catch (err) {
    localError.value =
      err?.message || err?.error?.message || 'Failed to create';
  } finally {
    isCreating.value = false;
  }
}

watch(
  () => [props.canAdd, props.addDisabled, props.disabled],
  ([can, addDisabled, disabled]) => {
    if (!can || addDisabled || disabled) cancelCreate();
  }
);
</script>

<style scoped>
.quick-add-select__action-btn {
  min-width: 2.5rem;
  width: 2.5rem;
}

.quick-add-select--creating :deep(.p-floatlabel label) {
  color: var(--p-primary-color);
}

.quick-add-select__group {
  max-width: 100%;
}

.quick-add-select__group :deep(.p-floatlabel) {
  min-width: 0;
}

.quick-add-select__group :deep(.p-select) {
  flex: 1 1 0%;
  min-width: 0;
  max-width: 100%;
}

.quick-add-select__group :deep(.p-select .p-select-label) {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quick-add-select__control--tag :deep(.p-select-label) {
  display: flex;
  align-items: center;
  min-height: 2.25rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}

.quick-add-select__control--rich :deep(.p-select-label) {
  display: flex;
  align-items: center;
  min-height: 2.5rem;
  padding-top: 0.35rem;
  padding-bottom: 0.35rem;
}

.quick-add-select__rich-option,
.quick-add-select__rich-value {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
  width: 100%;
}

.quick-add-select__rich-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--crc-text-strong, inherit);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quick-add-select__rich-meta {
  font-size: 0.75rem;
  color: var(--crc-text-muted, var(--p-text-muted-color));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quick-add-select__rich-value .quick-add-select__rich-meta {
  font-weight: 400;
}

.quick-add-select__group :deep(.p-inputtext) {
  flex: 1 1 0%;
  min-width: 0;
  max-width: 100%;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>
